import type { ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SortableRankedRowProps {
    id: string;
    position: number;
    name: string;
    onTap: () => void;
    badges?: ReactNode;
}

export function SortableRankedRow({ id, position, name, onTap, badges }: SortableRankedRowProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 50 : undefined,
        position: isDragging ? 'relative' as const : undefined,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                "flex items-center gap-2 px-3 py-2.5 bg-white",
                isDragging && "shadow-lg ring-2 ring-emerald-200 rounded-xl"
            )}
        >
            {/* Drag handle */}
            <div
                {...attributes}
                {...listeners}
                role="button"
                tabIndex={-1}
                className="touch-none p-2 -ml-2 text-slate-300 hover:text-slate-400 cursor-grab active:cursor-grabbing flex-shrink-0"
                style={{ touchAction: 'none' }}
            >
                <GripVertical size={16} />
            </div>

            {/* Position badge + name (tappable for actions) */}
            <button
                onClick={onTap}
                className="flex-1 min-w-0 flex items-center gap-3 text-left"
            >
                <span className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-black flex items-center justify-center flex-shrink-0">
                    {position}
                </span>
                <span className="text-sm font-semibold text-slate-800 truncate">{name}</span>
            </button>

            {/* Optional badges slot */}
            {badges}
        </div>
    );
}
